import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/posthog';

interface TourProgress {
  current_step: number;
  completed: boolean;
  paused: boolean;
  error_history: Array<{ step: number; error: string; timestamp: number }>;
}

export const useTourProgress = (userId: string | undefined) => {
  const [progress, setProgress] = useState<TourProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load progress from backend or localStorage
  useEffect(() => {
    const loadProgress = async () => {
      if (!userId) {
        // Load from localStorage for non-authenticated users
        const savedStep = localStorage.getItem('onboardingTourStep');
        const completed = localStorage.getItem('hasSeenOnboardingTour') === 'true';
        
        if (savedStep || completed) {
          setProgress({
            current_step: savedStep ? parseInt(savedStep, 10) : 0,
            completed,
            paused: !!savedStep,
            error_history: [],
          });
        }
        setIsLoading(false);
        return;
      }

      // Load from backend for authenticated users
      try {
        const { data, error } = await supabase
          .from('tour_progress')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setProgress({
            current_step: data.current_step,
            completed: data.completed,
            paused: data.paused,
            error_history: (data.error_history as any) || [],
          });

          // Sync to localStorage as backup
          if (!data.completed && data.paused) {
            localStorage.setItem('onboardingTourStep', data.current_step.toString());
          } else if (data.completed) {
            localStorage.setItem('hasSeenOnboardingTour', 'true');
            localStorage.removeItem('onboardingTourStep');
          }
        } else {
          // No backend record, check localStorage for migration
          const savedStep = localStorage.getItem('onboardingTourStep');
          const completed = localStorage.getItem('hasSeenOnboardingTour') === 'true';
          
          if (savedStep || completed) {
            setProgress({
              current_step: savedStep ? parseInt(savedStep, 10) : 0,
              completed,
              paused: !!savedStep,
              error_history: [],
            });
          }
        }
      } catch (error) {
        console.error('Error loading tour progress:', error);
        trackEvent('tour_sync_error', {
          error: error instanceof Error ? error.message : 'Unknown error',
          operation: 'load',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [userId]);

  // Save progress to backend and localStorage
  const saveProgress = useCallback(async (updates: Partial<TourProgress>) => {
    setIsSyncing(true);
    
    try {
      const newProgress = { ...progress, ...updates } as TourProgress;
      setProgress(newProgress);

      // Always save to localStorage as backup
      if (newProgress.completed) {
        localStorage.setItem('hasSeenOnboardingTour', 'true');
        localStorage.removeItem('onboardingTourStep');
      } else if (newProgress.paused) {
        localStorage.setItem('onboardingTourStep', newProgress.current_step.toString());
      } else if (!newProgress.paused) {
        localStorage.removeItem('onboardingTourStep');
      }

      // Save to backend if user is authenticated
      if (userId) {
        const { error } = await supabase
          .from('tour_progress')
          .upsert({
            user_id: userId,
            ...newProgress,
          }, {
            onConflict: 'user_id',
          });

        if (error) throw error;

        trackEvent('tour_progress_synced', {
          current_step: newProgress.current_step,
          completed: newProgress.completed,
          paused: newProgress.paused,
        });
      }
    } catch (error) {
      console.error('Error saving tour progress:', error);
      trackEvent('tour_sync_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
        operation: 'save',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [userId, progress]);

  // Clear progress from backend and localStorage
  const clearProgress = useCallback(async () => {
    setIsSyncing(true);
    
    try {
      localStorage.removeItem('hasSeenOnboardingTour');
      localStorage.removeItem('onboardingTourStep');

      if (userId) {
        const { error } = await supabase
          .from('tour_progress')
          .delete()
          .eq('user_id', userId);

        if (error) throw error;
      }

      setProgress(null);
      trackEvent('tour_progress_cleared');
    } catch (error) {
      console.error('Error clearing tour progress:', error);
      trackEvent('tour_sync_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
        operation: 'clear',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [userId]);

  return {
    progress,
    isLoading,
    isSyncing,
    saveProgress,
    clearProgress,
  };
};